import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _DateTimeUtils;
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// Can NOT import from DateUtils or will get circular dependency!
// Can NOT import from DateUtilsInternals, as it uses AbWebResources (which uses AbWebServices), so anytime
// there is a DateTimeUtils in the IWebServices transitive tree would get a cycle!
// Fixed via deferred loading in loadDeferredDateUtilsInternalFormatters
import { LocaleManager } from "abweb/service/locale/LocaleManager";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { DateFormatInternal } from "abweb/utils/date/internal/DateFormatInternal";
import { DateTimeFormatterDelegator } from "abweb/utils/date/DateTimeStructs";
import { DateUtilsIntlInternals } from "abweb/utils/date/internal/DateUtilsIntlInternals";
import { DateUtilsLuxonInternals } from "abweb/utils/date/internal/DateUtilsLuxonInternals";
import { setTestDate } from "abweb/utils/date/internal/CurrentDateFactory";
import { DateTime } from "luxon";
import * as DateUtilsInternals from "abweb/utils/date/internal/DateUtilsInternals";

///// PLEASE READ: This is meant to hold Date Formatters and Parsers ONLY!
// If you need to add some date-related utility functions, please talk to Brad about where they should go!

/**
 * Static helper class that holds DateTimeFormatter and DateTimeParser instances
 * for both localized and non-localized use cases.
 */
export class DateTimeUtils {
  static get datetimeLocale() {
    return DateTimeUtils._datetimeLocale;
  }

  /////// Locale Dependent Formatting ////////////////////////

  constructor() {
    // static class, can't create an instance
  }

  // Below are all computed (calculated) fields based on current datetimeFormatLocale (onLocaleChange recalculates them)

  /** format and Date instance as 10/6/2006 for en-US */

  /** format as 10/31/2006 for en-US (localized) */
  static get DATE_SHORT() {
    return this._DATE_SHORT;
  }

  /** Monday, October 06, 2006 for en-US (localized) */

  // Note timestamps use 'pad to 2' for fields so that timestamp length is uniform.  This helps timestamp output line up
  // for common use case of using timestamps at start of line or in a known column (table, csv, ...)

  /** format as Sunday, January 2, 2000 for en-US (localized) */
  static get DATE_LONG() {
    return this._DATE_LONG;
  }

  /** Oct 6 for en-US (localized) */

  /** Oct 6 for en-US (localized) */
  static get DATE_MONTH_AND_DAY() {
    return this._DATE_MONTH_AND_DAY;
  }

  /** 2:20 PM for en-US (localized) */

  /** format as 3:04 AM for en-US (localized) */
  static get TIME_WITH_MINUTES() {
    return this._TIME_WITH_MINUTES;
  }

  /** 2:20:34 PM for en-US (localized) */

  /** format as 3:04:05 AM for en-US (localized) */
  static get TIME_WITH_SECONDS() {
    return this._TIME_WITH_SECONDS;
  }

  /** 11/6/2000 12:00 AM for en-US (localized) */

  // WARNING: this must be the LAST static variable in the module in order to successfully initialize the other static vars (babel),
  // as otherwise this runs and then Babel (re-)initializes the other static variables with undefined (void 0)
  // Alternative: can use 'declare' syntax on the other uninitialized static variables

  /**  10/20/2000, 3:04 AM for en-US (localized) */
  static get DATETIME_WITH_MINUTES() {
    return this._DATETIME_WITH_MINUTES;
  }

  /** 11/6/2000, 12:00:00 AM for en-US (localized) */

  /** 10/20/2000, 3:04:05 AM for en-US (localized) */
  static get DATETIME_WITH_SECONDS() {
    return this._DATETIME_WITH_SECONDS;
  }

  /** 11/6/2000 12:00:00 AM for en-US (localized KENDO format) */

  /** 11/6/2000 12:00:00 AM for en-US (localized KENDO format) */
  static get DATETIME_WITH_SECONDS_NO_COMMA() {
    return this._DATETIME_WITH_SECONDS_NO_COMMA;
  }

  // Was unable to get DateTimeParser to work for _DATETIME_LONG, but no of no use case, so ignore for now...
  /** Monday, November 06, 2000 12:00:00 AM for en-US (localized) */

  //  & DateTimeParser;

  /** verbose format as 'Sunday, January 2, 2000 at 3:04:05 AM' for en-US (localized) */
  static get DATETIME_LONG() {
    return this._DATETIME_LONG;
  }

  ///////  Non-localized formatters/parsers below (do NOT have to be updated when locale changes) /////////

  /**
   * Returns a date formatted relative to the current date. If the delta between the two dates is more than seven days,
   * a short-date format is used. Otherwise, the date is reduced to something like "3 days ago". If the date is in
   * the future, a short-date format is used.
   * For example, Today, Yesterday, 1 week ago
   * @returns {string} The formatted date.
   */
  static get FORMAT_DATE_RELATIVE() {
    return DateTimeUtils._FORMAT_DATE_RELATIVE;
  }

  /**
   * Returns a date formatted relative to the current date. If the delta between the two dates is more than seven days,
   * a short-date format is used. Otherwise, the date is reduced to something like "3 minutes ago". If the date is in
   * the future, a short-date format is used.
   * For example, just now, or 25 minute ago.
   * @returns {string} The formatted date.
   */
  static get FORMAT_DATETIME_RELATIVE() {
    return DateTimeUtils._FORMAT_DATETIME_RELATIVE;
  }

  /**
   * Returns a date formatted relative to the current date in abbreviated form. If the delta between the two dates is more than one year,
   * will cut off as "1y+". Otherwise, the date is an abbreviated version of FORMATE_DATETIME_RELATIVE "3m ago". If the date is in
   * the future, a short-date format is used.
   * For example, just now, or 25m ago.
   * @returns {string} The formatted date.
   */
  static get FORMAT_DATETIME_RELATIVE_ABBREVIATED() {
    return DateTimeUtils._FORMAT_DATETIME_RELATIVE_ABBR;
  }

  /**
   * Returns a date formatted relative to the current date with time-only for today and "Yesterday Time"
   * for yesterday dates. If the date is from the current year, then "Month, Day Time" is returned. For
   * all other dates, including dates in the future, a short-date-time format is used.
   * @param value The date to format.
   * @param includeSeconds
   * @returns {string} The formatted date.
   */
  static formatRelativeDateTimeEx(value, includeSeconds) {
    return DateTimeUtils._FORMAT_RELATIVE_DATETIME_EX ? DateTimeUtils._FORMAT_RELATIVE_DATETIME_EX(value, includeSeconds) : DateTimeUtils.FORMATTER_NOT_LOADED_MSG;
  }

  /**
   * Calculates time difference between the two given dates and returns it formatted as in
   * "2 days 1 hour 20 minutes 35 seconds". The two date objects can be given in any order; that
   * is, it doesn't matter which one is earlier or later.
   * @param {Date} date1
   * @param {Date} date2
   */
  static formatDifference(date1, date2) {
    return DateTimeUtils._FORMAT_DIFFERENCE ? DateTimeUtils._FORMAT_DIFFERENCE(date1, date2) : DateTimeUtils.FORMATTER_NOT_LOADED_MSG;
  }

  /////   DateTimeFormatter and DateTimeParser Factory Methods  //////

  // Luxon factory methods
  /**
   * @param luxonFormat luxon format string used to format a Date as a string.  Supported luxon formats
   * are documented in: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
   * @return DateTimeFormatter instance that will format a date according to the provided luxonFormat string.
   */

  /////  Configuration methods, including changing locale support ///////

  /**
   * Sets the current locale for formatting
   *
   * @param newDatetimeLocale
   */
  static onLocaleChange(newDatetimeLocale) {
    if (newDatetimeLocale === DateTimeUtils._datetimeLocale) {
      return;
    }
    DateTimeUtils._datetimeLocale = newDatetimeLocale;
    console.log(`DateTimeUtils.datetimeLocale is '${newDatetimeLocale}'`);
    // Ensure DateFormatters/DateParsers are built with the correct locale
    DateUtilsLuxonInternals.onLocaleChange(DateTimeUtils._datetimeLocale);

    // The formatting functions all have the datetimeLocale baked in to the DateFormatter (curried),
    // so recalc all locale-dependent DateFormatters
    const buildIntlFAndP = DateUtilsIntlInternals.buildIntlDateTimeFormatterAndParser;
    DateTimeUtils._DATE_SHORT = buildIntlFAndP(DateTime.DATE_SHORT);
    DateTimeUtils._DATE_MONTH_AND_DAY = buildIntlFAndP({
      month: "short",
      day: "numeric"
    });
    /** Monday, October 06, 2006 for en-US */
    DateTimeUtils._DATE_LONG = buildIntlFAndP(DateTime.DATE_HUGE);
    /** 2:20 PM for en-US */
    DateTimeUtils._TIME_WITH_MINUTES = buildIntlFAndP(DateTime.TIME_SIMPLE);
    /** 2:20:34 PM for en-US */
    DateTimeUtils._TIME_WITH_SECONDS = buildIntlFAndP(DateTime.TIME_WITH_SECONDS);

    // Existing Ab Initio (kendo) standard is to NOT have a comma separator between date and time parts.
    // Unfortunately, both Intl.DateTimeFormatOptions and luxon localized grammars (F, FF) both have a comma separator.
    //
    // Need special processing to support backward compatible convention of no comma separator.
    // To do this, process the val string to remove/add comma when formatted/parsed.
    // Known bug if the resulting parse exception is directly shown to the user in that the 'parse value' part of the
    // exception will have the comma in it even though user didn't type it (could add 'fixValue' method to
    // DateTimeParser interface if really care...).  How often to people input localized datetime strings manually?

    /** 11/6/2000, 12:00 AM for en-US  */
    DateTimeUtils._DATETIME_WITH_MINUTES = buildIntlFAndP(DateTime.DATETIME_SHORT /*aka luxon 'F'*/ /*, true*/);
    /** 11/6/2000, 12:00:00 AM for en-US */
    DateTimeUtils._DATETIME_WITH_SECONDS = buildIntlFAndP(DateTime.DATETIME_SHORT_WITH_SECONDS /*aka luxon 'FF'*/ /*, true*/);
    /** 11/6/2000 12:00:00 AM for en-US */
    DateTimeUtils._DATETIME_WITH_SECONDS_NO_COMMA = buildIntlFAndP(DateTime.DATETIME_SHORT_WITH_SECONDS /*aka luxon 'FF'*/, true);

    /** Monday, November 06, 2000, 12:00:00 AM for en-US */
    DateTimeUtils._DATETIME_LONG =
    // No, loses 'localized order': DateUtilsLuxonInternals.buildDateTimeFormatter("EEEE, DDD tt");
    // Below for 'en' puts an 'at' between date and time parts: '... at 12:34:56'
    buildIntlFAndP(
    // DateTime.HUGE_WITH_SECONDS has timezone, which we don't want for backward compatibility,
    // so reset the timezone to undefined
    {
      ...DateTime.DATETIME_HUGE_WITH_SECONDS,
      timeZoneName: undefined
    });

    // Below onLocaleChange that MUST happen after DateTimeUtils.onLocaleChange and DateUtilsLuxonInternals.onLocaleChange
    DateFormatInternal.onLocaleChange(DateTimeUtils._datetimeLocale);
  }
  static async resetStatics() {
    setTestDate(null);
    LocaleManager.registerAndInvokeSyncLocaleListener(localeState => DateTimeUtils.onLocaleChange(localeState.datetimeFormatLocale));

    // higher-level formatters depends on localized formatters set above
    await DateTimeUtils.loadDeferredDateUtilsInternalFormatters();
  }
  static async loadDeferredDateUtilsInternalFormatters() {
    if (!DateTimeUtils._FORMAT_RELATIVE_DATETIME_EX) {
      // Formerly had issue when loading web apps where got a cycle, and broke it via a dynamic import of DateUtilsInternals.
      // However, later removed all other imports of DUI, and was able to make it a static import again (top of this file).
      // The cypress test iframe was barfing on the dynamic DUI import (pulled in via ab_cy.ts::ProfileUtils), which
      // was 'fixed' when reverted back to static import.  If the cypress test iframe starts to barf again, one
      // (very ugly) hack may be able to put a static import of DUI as the first import in ab_cy.py, OR to experiment
      // with injecting I18NService into DUI after i18nService isInitialized (probably do it in AbWebServicesInitializer).

      // const DateUtilsInternals = await import("abweb/utils/date/internal/DateUtilsInternals");
      DateTimeUtils._FORMAT_DATE_RELATIVE = DateUtilsInternals.formatRelativeDate;
      DateTimeUtils._FORMAT_DATETIME_RELATIVE = DateUtilsInternals.formatRelativeDateTime;
      DateTimeUtils._FORMAT_DATETIME_RELATIVE_ABBR = DateUtilsInternals.formatRelativeDateTimeAbbreviated;
      DateTimeUtils._FORMAT_DIFFERENCE = DateUtilsInternals.formatDifference;
      DateTimeUtils._FORMAT_RELATIVE_DATETIME_EX = DateUtilsInternals.formatRelativeDateTimeEx;
    }
  }

  ////////////////// DateFormat Support ///////////////////////

  /**
   * Formats a Date according to the specified AbInitioDateFormat.
   * @param value {Date} object.  If null, empty string is returned.
   * @param abInitioFormat {AbInitioDateFormat}
   */
  static formatValue(value, abInitioFormat) {
    return DateFormatInternal.formatValue(value, abInitioFormat, false);
  }

  /**
   * @deprecated Use Luxon-based formatValue instead.
   * Formats a Date according to the specified KendoDateFormat.
   * This will be removed at anytime (only 1 current usage by the
   * deprecated ui.form.AbDateInput (via AbDateInputUtils.getFormatedValue).
   * @param value {Date} object.  If null, empty string is returned.
   * @param kendoFormat {KendoDateFormat}
   */
  static formatKendoValue(value, kendoFormat) {
    return DateFormatInternal.formatValue(value, kendoFormat, true);
  }

  /**
   * @param abInitioFormat {AbInitioDateFormat}
   * @return formatter corresponding to abInitioFormat value
   */
  static buildDateTimeFormatter(abInitioFormat) {
    return DateFormatInternal.buildAbInitioDateTimeFormatter(abInitioFormat);
  }

  /**
   * @deprecated Use Luxon-based buildDateTimeFormatter instead.
   * @param kendoFormat {KendoDateFormat}
   * @return formatter corresponding to kendoFormat value
   */
  static buildKendoDateTimeFormatter(kendoFormat) {
    return DateFormatInternal.buildAbInitioDateTimeFormatter(kendoFormat, true);
  }

  // Do NOT expose DateFormatInternal.buildAbInitioDateTimeFormatterAndParser as no document use cases,
  // and it is a bit sketchy in that it uses the format string as the parse string (which *mostly* works)

  /**
   * @deprecated Only used by kendo widgets that are being removed
   * @return corresponding kendo format string for given DateFormatter function.
   * It must be one of the DateFormatters that handle the formatting for the AbInitioCustomFormat values.
   */
  static getKendoFormatForDateFormatter(dateFormatter) {
    return DateFormatInternal.FormatterToKendoParseFormat.get(dateFormatter);
  }

  /**
   * @deprecated
   * This is functionally equivalent to the old kendo.parseDate function,
   * and will be removed when the last kendo widgets are removed.
   *
   * @return parsed date from string.  It returns the first successful parse of the following formats:
   * - localized date
   * - yyyy-MM-dd
   * - localized datetime with second precision (WITHOUT comma)
   * - attempt to split out just the date part and parse as localized date or yyyy-MM-dd
   * If it fails to parse via any of the above, it returns null.
   *
   * Note that the signature of kendoParseDate is more accurate than the original kendo.parseDate(string): Date,
   * which lied as it accepts string | null | undefined as an arg, and would return either Date or null
   * if it was unable to parse.  The kendoParseDate behaves the same, and has a signature to match,
   * so caller may need to override the more accurate return value to be behaviorally consistent
   * with original kend.parseDate call.
   */
  static kendoParseDate(val) {
    // MHDatePropertyUSpec tests (and prod code?) require non-localized DATE_YYYY_MM_DD format support
    const DateOnlyParsers = [DateTimeUtils.DATE_SHORT, DateTimeUtils.DATE_YYYY_MM_DD];

    /** @returns first successfully parsed Date, or null if all parsers fail */
    function parseTries(val, parsers) {
      for (const parser of parsers) {
        let parseRetVal = parser.parse(val);
        if (ClassUtils.isInstanceOf(parseRetVal, Date)) return parseRetVal;
      }
      return null;
    }
    val = val?.trim();
    if (!val) return null;
    const parseRetVal = parseTries(val, [...DateOnlyParsers, DateTimeUtils.DATETIME_WITH_SECONDS_NO_COMMA]);
    if (parseRetVal) return parseRetVal;

    // Fallback: kendo.parseDate tries to parse just the 'date part' of a datetime string if it can't parse
    // the whole thing as a datetime, so use the same fallback strategy for consistency.
    const datetimeSepIndex = val.indexOf(" "); // space separates date and time parts
    return datetimeSepIndex !== -1 ? parseTries(val.substring(0, datetimeSepIndex), DateOnlyParsers) // return Date|null
    : null;
  }
}

// Outside of class to guarantee that it is 'last' (even when intellij reorders static class members!)
// noinspection JSUnusedLocalSymbols
_DateTimeUtils = DateTimeUtils;
/** Non-localized yyyy-MM-dd string with month and day padded to 2 chars:  2022-08-31 */
_defineProperty(DateTimeUtils, "DATE_YYYY_MM_DD", DateUtilsLuxonInternals.buildDateTimeFormatterAndParser("yyyy-MM-dd"));
// noinspection JSUnusedGlobalSymbols
/** Non-localized timestamp to minute precision, Months, days, hours, minutes fields padded to 2 chars, 24hr:  2022-08-31 23:59 */
_defineProperty(DateTimeUtils, "FORMAT_TIMESTAMP_MIN", DateUtilsLuxonInternals.buildDateTimeFormatter("yyyy-MM-dd HH:mm"));
//////   Locale-dependent Formatters - recalc on locale change //////////
// noinspection JSUnusedGlobalSymbols
/** Non-localized timestamp to second precision, Months, days, hours, minutes, seconds fields padded to 2 chars, 24hr:  2022-12-31 23:59:59 */
_defineProperty(DateTimeUtils, "FORMAT_TIMESTAMP_SEC", DateUtilsLuxonInternals.buildDateTimeFormatter("yyyy-MM-dd HH:mm:ss"));
/** Non-localized timestamp to second precision suitable for being part of a filename: 'yyyyMMdd-HHmmss' so:  20221231-235959 */
_defineProperty(DateTimeUtils, "FORMAT_FILENAME_TIMESTAMP_SEC", DateUtilsLuxonInternals.buildDateTimeFormatter("yyyyMMdd-HHmmss"));
/** Non-localized timestamp to msecond precision, Months, days, hours, minutes, seconds fields padded to 2 chars, 24hr:  2022-12-31 23:59:59.123 */
_defineProperty(DateTimeUtils, "FORMAT_TIMESTAMP_MSEC", DateUtilsLuxonInternals.buildDateTimeFormatter("yyyy-MM-dd HH:mm:ss.SSS"));
/** timestamp with sec precision: "M/d/yy H:mm:ss".   10/31/22 14:15:16    Often used with CSV output. */
_defineProperty(DateTimeUtils, "FORMAT_TIMESTAMP_CSV", DateUtilsLuxonInternals.buildDateTimeFormatter("M/d/yy H:mm:ss"));
_defineProperty(DateTimeUtils, "FORMATTER_NOT_LOADED_MSG", "Formatter not loaded yet!");
_defineProperty(DateTimeUtils, "NotLoadedDateFormatter", new DateTimeFormatterDelegator(val => _DateTimeUtils.FORMATTER_NOT_LOADED_MSG));
_defineProperty(DateTimeUtils, "_FORMAT_DATE_RELATIVE", _DateTimeUtils.NotLoadedDateFormatter);
_defineProperty(DateTimeUtils, "_FORMAT_DATETIME_RELATIVE", _DateTimeUtils.NotLoadedDateFormatter);
_defineProperty(DateTimeUtils, "_FORMAT_DATETIME_RELATIVE_ABBR", _DateTimeUtils.NotLoadedDateFormatter);
_defineProperty(DateTimeUtils, "_FORMAT_DIFFERENCE", void 0);
_defineProperty(DateTimeUtils, "_FORMAT_RELATIVE_DATETIME_EX", void 0);
// Holds native browser datetime locale.   Real implementation (SOR) will be backed by LocaleManager.datetimeFormatLocale
_defineProperty(DateTimeUtils, "_datetimeLocale", void 0);
_defineProperty(DateTimeUtils, "_DATE_SHORT", void 0);
_defineProperty(DateTimeUtils, "_DATE_LONG", void 0);
_defineProperty(DateTimeUtils, "_DATE_MONTH_AND_DAY", void 0);
_defineProperty(DateTimeUtils, "_TIME_WITH_MINUTES", void 0);
_defineProperty(DateTimeUtils, "_TIME_WITH_SECONDS", void 0);
_defineProperty(DateTimeUtils, "_DATETIME_WITH_MINUTES", void 0);
_defineProperty(DateTimeUtils, "_DATETIME_WITH_SECONDS", void 0);
_defineProperty(DateTimeUtils, "_DATETIME_WITH_SECONDS_NO_COMMA", void 0);
_defineProperty(DateTimeUtils, "_DATETIME_LONG", void 0);
_defineProperty(DateTimeUtils, "buildLuxonDateTimeFormatter", DateUtilsLuxonInternals.buildDateTimeFormatter);
/**
 * @param luxonFormat luxon format string used to format a Date as a string.  Supported luxon formats
 * are documented in: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
 * @param luxonParseFormat luxon parse format string used to parse a string as a Date(| time | datetime).
 * Defaults to luxonFormat if not provided as most times the luxonFormat and luxonParseFormat strings are the same
 * (but note that there are luxon format tokens that* aren't valid as parsing tokens).  Support luxon parsing formats
 * are documented in: https://github.com/moment/luxon/blob/master/docs/parsing.md#table-of-tokens
 * @return instance that can be used for both formatting and parsing of Date instances
 */
_defineProperty(DateTimeUtils, "buildLuxonDateTimeFormatterAndParser", DateUtilsLuxonInternals.buildDateTimeFormatterAndParser);
// Intl.DateFormat factory methods
// noinspection JSUnusedGlobalSymbols
/**
 * This returns a localized datetime formatter that leverages the javascript Intl.DateTimeFormat functionality for
 * localization - see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 * @param options - controls the localized format of the returned string.  See
 * https://tc39.es/ecma402/#table-datetimeformat-resolvedoptions-properties for supported option values.
 * @returns DateTimeFormatter instance that formats according to the provided options
 */
_defineProperty(DateTimeUtils, "buildIntlDateTimeFormatter", DateUtilsIntlInternals.buildIntlDateTimeFormatter);
// noinspection JSUnusedGlobalSymbols
/**
 * This returns a localized datetime formatter AND parser that leverages the javascript Intl.DateTimeFormat functionality for
 * localization - see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 *
 * Note: not every set of provided options can be parsed effectively - as such, it is better to use a prebuilt
 * DateTimeFormatter & DateTimeParser instances that are available as static member of
 * DateTimeUtils.{DATE | TIME | DATETIME}_* (e.g., DateTimeUtils.DATE_SHORT).
 *
 * @param options - controls the localized format of the returned string.  See
 * https://tc39.es/ecma402/#table-datetimeformat-resolvedoptions-properties for supported option values.
 * @returns DateTimeFormatter instance that formats according to the provided options,
 * and a corresponding DateTimeParser will parse a string that has been formatted according to the provided options.
 */
_defineProperty(DateTimeUtils, "buildIntlDateTimeFormatterAndParser", DateUtilsIntlInternals.buildIntlDateTimeFormatterAndParser);
DateTimeUtils.resetStatics();
//# sourceMappingURL=DateTimeUtils.js.map
